import { useState, useEffect } from 'react';
import { UniversalWindow } from '@/components/ui/UniversalWindow';
import { AutoSelectionForm } from '@/components/autoforms/AutoSelectionForm';
import { resolveObjectSynonym } from '@/lib/resolveObjectName';

// ============================================================================
// ObjectFormDialogs — диалоговые оверлеи формы объекта (вынесено из
// AutoObjectForm, E2e6): пикер ссылочного поля (PickerWindow), «Любая ссылка»
// (тип → значение/файл), «Поле множественного значения» ТЧ, подбор модуля
// формы. ЧИСТО отображательный компонент (SRP): состояние и ВСЯ запись в
// formStore/стор данных остаются в AutoObjectForm — сюда передаются колбэки.
// ============================================================================

export interface PickerAttrRef {
  name: string;
  types: Array<{ typeName: string; fullName: string; objectType: string }>;
  tsName?: string;
  rowIndex?: number;
  selectMode?: string;
  filters?: Array<{ field: string; condition: string; value?: string }>;
  createInitialData?: Record<string, unknown>;
}

export interface AnyRefTypeOption {
  objectType: string;
  objectName: string;
  name: string;
}

export interface AnyRefPickerState {
  field: string;
  label: string;
  types: AnyRefTypeOption[];
  pickingType: { objectType: string; objectName: string } | null;
}

export interface ModulePickerState {
  open: boolean;
  objectType: string;
  objectName: string;
  callback: (result: { id: string; name: string; objectType: string; objectName: string }) => void;
}

export interface MultiPickState {
  tsName: string;
  colMeta: { name?: string; label?: string; refTable?: string };
}

export interface ModulePickerResult {
  id: string;
  name: string;
  objectType: string;
  objectName: string;
}

interface ObjectFormDialogsProps {
  pickingAttr: PickerAttrRef | null;
  anyRefPicker: AnyRefPickerState | null;
  multiPick: MultiPickState | null;
  modulePicker: ModulePickerState | null;
  onPickerClose: () => void;
  onPickerSelect: (id: string, name: string) => void;
  onAnyRefClose: () => void;
  onAnyRefTypeSelect: (t: { objectType: string; objectName: string }) => void;
  onAnyRefValueSelect: (id: string, name: string) => void;
  onAnyRefFile: (file: File) => void;
  onMultiClose: () => void;
  onMultiSelect: (id: string, name: string) => void;
  onModuleClose: () => void;
  onModuleSelect: (id: string, name: string) => void;
}

export function ObjectFormDialogs({
  pickingAttr, anyRefPicker, multiPick, modulePicker,
  onPickerClose, onPickerSelect,
  onAnyRefClose, onAnyRefTypeSelect, onAnyRefValueSelect, onAnyRefFile,
  onMultiClose, onMultiSelect,
  onModuleClose, onModuleSelect,
}: ObjectFormDialogsProps) {
  return (
    <>
      {pickingAttr && (() => {
        const ref = parseRefType(pickingAttr.types);
        if (!ref) return null;
        return (
          <PickerWindow
            open
            onClose={onPickerClose}
            objectType={ref.objectType}
            objectName={ref.objectName}
            blocking
            width="max-w-2xl"
          >
            <AutoSelectionForm
              objectType={ref.objectType}
              objectName={ref.objectName}
              onSelect={onPickerSelect}
              selectionMode={pickingAttr.selectMode || ''}
              filters={pickingAttr.filters as any}
              createInitialData={pickingAttr.createInitialData}
            />
          </PickerWindow>
        );
      })()}
      {anyRefPicker && (
        <UniversalWindow
          open
          onClose={onAnyRefClose}
          title={`${anyRefPicker.label}: выбор типа и значения`}
          blocking
          width="max-w-2xl"
          height="auto"
        >
          {!anyRefPicker.pickingType ? (
            <div className="p-3 space-y-1 max-h-96 overflow-y-auto">
              <div className="text-[11px] text-muted-foreground mb-1">Выберите тип объекта:</div>
              {anyRefPicker.types.map((t) => (
                <button
                  key={`${t.objectType}/${t.objectName}`}
                  className="w-full text-left px-3 py-1.5 text-xs border rounded hover:bg-accent"
                  onClick={() => onAnyRefTypeSelect({ objectType: t.objectType, objectName: t.objectName })}
                >
                  {t.name}
                </button>
              ))}
            </div>
          ) : anyRefPicker.pickingType.objectType === 'file' ? (
            // Файл в «Любой ссылке»: загрузка в хранилище (FileService, SSoT),
            // значение = id файла, {col}_table = системный справочник файлов
            <div className="p-3 space-y-2">
              <div className="text-xs text-muted-foreground">Загрузите файл (хранится в системном хранилище)</div>
              <input
                type="file"
                className="text-xs"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) onAnyRefFile(file);
                }}
              />
            </div>
          ) : (
            <AutoSelectionForm
              objectType={anyRefPicker.pickingType.objectType}
              objectName={anyRefPicker.pickingType.objectName}
              onSelect={onAnyRefValueSelect}
            />
          )}
        </UniversalWindow>
      )}
      {multiPick && (
        <UniversalWindow
          open
          onClose={onMultiClose}
          title={`Выбор: ${multiPick.colMeta?.label || multiPick.colMeta?.name || ''}`}
          blocking
          width="max-w-2xl"
        >
          <AutoSelectionForm
            objectType="catalog"
            objectName={multiPick.colMeta?.refTable || multiPick.colMeta?.name || ''}
            onSelect={onMultiSelect}
          />
        </UniversalWindow>
      )}
      {modulePicker && (
        <UniversalWindow
          open
          onClose={onModuleClose}
          title={`Подбор: ${modulePicker.objectName}`}
          blocking
          width="max-w-2xl"
        >
          <AutoSelectionForm
            objectType={modulePicker.objectType}
            objectName={modulePicker.objectName}
            onSelect={onModuleSelect}
          />
        </UniversalWindow>
      )}
    </>
  );
}

/** Первый тип из набора (SSoT: рендер формы выбора по типу ссылки). */
function parseRefType(types: Array<{ typeName: string; fullName: string; objectType: string }>): { objectType: string; objectName: string } | null {
  if (!types || types.length === 0) return null;
  const t = types[0];
  const full = t.fullName || t.typeName || '';
  const dot = full.indexOf('.');
  const objectName = dot >= 0 ? full.slice(dot + 1) : full;
  return { objectType: t.objectType || 'catalog', objectName };
}

// ============================================================================
// PickerWindow — окно выбора значения (форма выбора): заголовок
// «Выбор: <СИНОНИМ>» (резолв по типу/коду — SSoT сервер; не транслит-код).
// ============================================================================
function PickerWindow({ open, onClose, objectType, objectName, blocking, width, children }: {
  open: boolean; onClose: () => void; objectType: string; objectName: string;
  blocking?: boolean; width?: string; children: React.ReactNode;
}) {
  const [syn, setSyn] = useState(objectName);
  useEffect(() => {
    let cancelled = false;
    setSyn(objectName);
    void resolveObjectSynonym(objectType, objectName).then((name) => { if (!cancelled) setSyn(name); });
    return () => { cancelled = true; };
  }, [objectType, objectName]);
  return (
    <UniversalWindow open={open} onClose={onClose} title={`Выбор: ${syn}`} blocking={blocking} width={width}>
      {children}
    </UniversalWindow>
  );
}
